import { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import heroLighting from '@assets/hero-lighting.png';
import heroSkating from '@assets/skating-rink.png';
import { Header } from '@components/layout/header';
import { Footer } from '@components/layout/Footer';
import ErrorBoundary from '@components/errors/ErrorBoundary';
import { ToastProvider } from '@components/common/ToastProvider';
import IntroAnimation from '@components/IntroAnimation';
import ScrollToTop from '@components/ScrollToTop';
import { PUBLIC_PHONE } from '@lib/businessContact';

/* Homepage stays in the first load. Other routes load when opened. */
import { Home } from '@pages/Home';

const About = lazy(() => import('@pages/About').then(module => ({ default: module.About })));
const Services = lazy(() => import('@pages/Services').then(module => ({ default: module.Services })));
const ServiceDetail = lazy(() => import('@pages/ServiceDetail').then(module => ({ default: module.ServiceDetail })));
const GalleryPage = lazy(() => import('@pages/GalleryPage').then(module => ({ default: module.GalleryPage })));
const ServiceArea = lazy(() => import('@pages/ServiceArea').then(module => ({ default: module.ServiceArea })));
const Contact = lazy(() => import('@pages/Contact').then(module => ({ default: module.Contact })));
const Projects = lazy(() => import('@pages/Projects').then(module => ({ default: module.Projects })));
const ProjectDetail = lazy(() => import('@pages/ProjectDetail').then(module => ({ default: module.ProjectDetail })));
const Guides = lazy(() => import('@pages/Guides').then(module => ({ default: module.Guides })));
const GuideDetail = lazy(() => import('@pages/GuideDetail').then(module => ({ default: module.GuideDetail })));
const Privacy = lazy(() => import('@pages/Privacy').then(module => ({ default: module.Privacy })));
const NotFound = lazy(() => import('@pages/NotFound').then(module => ({ default: module.NotFound })));

export function App() {
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [introComplete, setIntroComplete] = useState(false);

  const handleIntroComplete = useCallback(() => {
    setIntroComplete(true);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 500);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  return (
    <Router>
      <ScrollToTop />
      <IntroAnimation onComplete={handleIntroComplete} />
      <ToastProvider />
      <ErrorBoundary>
        <div className="relative">
          <Header />
          <main id="main-content" className="relative min-h-screen">
            <Suspense fallback={<div className="min-h-screen bg-[#0F0F23]" />}>
              <Routes>
                <Route path="/" element={<Home introComplete={introComplete} heroImage={heroLighting} heroImageSecondary={heroSkating} />} />
                <Route path="/about" element={<About />} />
                <Route path="/services" element={<Services />} />
                <Route path="/services/:serviceSlug" element={<ServiceDetail />} />
                <Route path="/gallery" element={<GalleryPage />} />
                <Route path="/projects" element={<Projects />} />
                <Route path="/projects/:slug" element={<ProjectDetail />} />
                <Route path="/guides" element={<Guides />} />
                <Route path="/guides/:slug" element={<GuideDetail />} />
                <Route path="/service-area" element={<ServiceArea />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </main>

          <Footer />

          {/* Floating Call Button — uses existing published 386 line until a single primary is confirmed */}
          <a
            href={PUBLIC_PHONE.tel}
            className="floating-call-btn bg-[#7C3AED] text-white p-4 rounded-full shadow-lg hover:bg-[#6D28D9] transition-colors"
            aria-label={`Call us at ${PUBLIC_PHONE.display}`}
          >
            <i className="fas fa-phone text-xl" aria-hidden="true"></i>
          </a>

          {/* Back to Top Button */}
          {showBackToTop && (
              <button
                onClick={scrollToTop}
                className="back-to-top bg-[#1E1B4B] text-white p-3 rounded-full shadow-lg hover:bg-[#0F0F23] transition-all border border-[#7C3AED]/30"
                aria-label="Back to top"
              >
                <i className="fas fa-arrow-up text-lg" aria-hidden="true"></i>
              </button>
          )}
        </div>
      </ErrorBoundary>
    </Router>
  );
}
 